import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getScheduledChangesCount } from "../../services/scheduledChangesApi";

const resolveCount = (payload) => {
    if (Number.isFinite(payload)) return payload;

    const value = payload?.count ?? payload?.total ?? payload?.totalCount ?? payload?.data;
    return Number.isFinite(value) ? value : 0;
};

const ScheduledChangesCard = () => {
    const [pendingCount, setPendingCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const subtitle =
        "Zmiany zaplanowane do wykonania w określonym czasie";

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;

        (async () => {
            setIsLoading(true);
            setError(null);

            try {
                const response = await getScheduledChangesCount("Approved", controller.signal);

                if (cancelled) return;

                const payload = response?.data;
                setPendingCount(resolveCount(payload));
            } catch (e) {
                if (cancelled) return;
                if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
                setError(e);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, []);

    const statusBadge = useMemo(() => {
        if (isLoading) {
            return (
                <Badge bg="light" text="dark" className="waiting-changes-card__badge">
                    Ładowanie…
                </Badge>
            );
        }

        if (error) {
            return (
                <Badge bg="danger" className="waiting-changes-card__badge">
                    Błąd API
                </Badge>
            );
        }

        return (
            <Badge bg="warning" text="dark" className="waiting-changes-card__badge">
                Oczekujące
            </Badge>
        );
    }, [error, isLoading]);

    return (
        <Card className="waiting-changes-card border-0 shadow-sm">
            <Card.Body className="d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                        <Card.Title as="h5" className="waiting-changes-card__title mb-1">
                            Zaplanowane zmiany
                        </Card.Title>
                        <Card.Text className="waiting-changes-card__subtitle mb-0">
                            {subtitle}
                        </Card.Text>
                    </div>
                    {statusBadge}
                </div>

                <div className="waiting-changes-card__metric-wrap mt-2 mb-2">
                    {isLoading ? (
                        <div className="d-flex align-items-center gap-2 text-muted">
                            <Spinner size="sm" animation="border" />
                            <span>Pobieranie danych…</span>
                        </div>
                    ) : (
                        <div className="waiting-changes-card__metric">
                            <span className="waiting-changes-card__metric-number">
                                {pendingCount}
                            </span>
                            <span className="waiting-changes-card__metric-label">zaplanowanych</span>
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-end">
                    <Button
                        as={Link}
                        to="/scheduled-changes"
                        variant="dark"
                        size="sm"
                        className="waiting-changes-card__cta"
                    >
                        Otwórz listę
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default ScheduledChangesCard;
